import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { companyPolicyApi } from "EmployerDashboard/api/companyPolicy";
import { ENTITY_ACTIONS } from "EntitiesApi/Actions";
import { entitiesReducer } from "EntitiesApi/Reducers";
import { rootSaga as apiSaga } from "EntitiesApi/Sagas";
import { ENTITY_PATHS } from "EntitiesApi/Selectors";
import { intuitApi } from "Intuit/api";
import { largeGroupSurveyApi } from "LargeGroupIntent/api";
import honeybadgerMiddleware from "Redux/Middleware/honeybadgerMiddleware";
import { reducer as formReducer } from "redux-form";
import createSagaMiddleware from "redux-saga";
import { fork } from "redux-saga/effects";

import { employeeProfileReducer, employeeProfileOverallErrorReducer } from "Common/EnterAges/Reducers";
import { EmployeeProfileSaga, validateOnChange as EmployeeProfileValidateOnChange } from "Common/EnterAges/Sagas";

import { accountId, companyPolicyId, siteConfig, manageEmployeesTab, offerCoverageSuccessMessage } from "./reducers";

import { accountApi } from "Care/features/account";
import { chatApi } from "Care/features/chat";
import { quoteApi } from "Care/features/quote";

import type { EntityTable } from "EntitiesApi/Reducers";
import type { SiteConfig, Account, CompanyPolicy, CompanyProfile, EmployeeDependent, EmployeeForm, EmployeePolicy, EmployerForm, Plan } from "Utils/types";

export type State = {
  accountId: string;
  companyPolicyId: string;
  employeeProfile: any;
  employeeProfileOverallError: {
    errorMessage: null | undefined | string;
    eeErrorIndex: null | undefined | number;
    eeDependentErrorIndex: null | undefined | number;
  };
  entities: {
    accounts: EntityTable<Account>;
    companyPolicies: EntityTable<CompanyPolicy>;
    companyProfiles: EntityTable<CompanyProfile>;
    employeeDependents: EntityTable<EmployeeDependent>;
    employeeForms: EntityTable<EmployeeForm>;
    employeePolicies: EntityTable<EmployeePolicy>;
    employerForms: EntityTable<EmployerForm>;
    medicalPlans: EntityTable<Plan>;
    dentalPlans: EntityTable<Plan>;
    visionPlans: EntityTable<Plan>;
  };

  form: any;
  siteConfig: SiteConfig;
  manageEmployeesTab: string;
  offerCoverageSuccessMessage: string;
};

export type Action = {
  readonly type: string;
  readonly payload: any;
};

export const rootReducer = combineReducers({
  accountId,
  companyPolicyId,
  employeeProfile: employeeProfileReducer,
  employeeProfileOverallError: employeeProfileOverallErrorReducer,
  entities: entitiesReducer({
    accounts: {},
    companyPolicies: {},
    companyProfiles: {},
    employeeDependents: {},
    employeeForms: {},
    employeePolicies: {},
    employerForms: {},
    medicalPlans: {},
    dentalPlans: {},
    visionPlans: {},
  }) as any,
  [accountApi.reducerPath]: accountApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [intuitApi.reducerPath]: intuitApi.reducer,
  [quoteApi.reducerPath]: quoteApi.reducer,
  [companyPolicyApi.reducerPath]: companyPolicyApi.reducer,
  [largeGroupSurveyApi.reducerPath]: largeGroupSurveyApi.reducer,
  form: formReducer,
  siteConfig,
  manageEmployeesTab,
  offerCoverageSuccessMessage,
});

const sagaMiddleware = createSagaMiddleware();

function* rootSaga() {
  yield [fork(apiSaga), fork(EmployeeProfileSaga), fork(EmployeeProfileValidateOnChange)];
}

const createStore = (initialState: Partial<State>) => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // New with RTK - builtin checks for type safety
        // Entities API works, but throws serializer errors due to this
        // Disabling the serializable check suppresses the benign error
        // Future: Migrate away from Entities API and towards RTK
        serializableCheck: {
          ignoredActions: [...ENTITY_ACTIONS],
          ignoredPaths: [...ENTITY_PATHS, "form.submitClaims.values.claim.file"],
        },
      })
        .concat(sagaMiddleware)
        .concat(honeybadgerMiddleware)
        .concat(accountApi.middleware)
        .concat(chatApi.middleware)
        .concat(intuitApi.middleware)
        .concat(companyPolicyApi.middleware)
        .concat(quoteApi.middleware)
        .concat(largeGroupSurveyApi.middleware),
    preloadedState: initialState,
  });

  sagaMiddleware.run(rootSaga);
  return store;
};

export default createStore;

export type Store = ReturnType<typeof createStore>;
export type Dispatch = ReturnType<typeof createStore>["dispatch"];
